import { useEffect, useState } from "react";
import useIsMobile from "../../../../hooks/useIsMobile";
import { format } from "date-fns";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Image } from "primereact/image";
import { Skeleton } from "primereact/skeleton";
import { motion, AnimatePresence } from "framer-motion";
import { Toast } from "primereact/toast";

const CustomerList = (props) => {
  const { data, filterValue, loading, paging, handleOnPage } = props;
  const [viewType, setViewType] = useState("card");
  const viewOptions = [
    { label: "Card View", value: "card" },
    { label: "Table View", value: "table" },
  ];
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const isMobile = useIsMobile(750);

  useEffect(() => {
    if (isMobile) {
      setViewType("card");
    } else {
      setViewType("table");
    }
  }, [isMobile]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatPax = (value) => `${value} pax`;

  const CustomerMobileTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const rank = rowIndex + 1 + paging.take * (paging.page - 1);
    return (
      <div className="row-start-center w-full gap-4">
        <span
          className={`min-w-[1rem] min-h-[1rem] w-[1rem] h-[1rem] sm:min-w-[3rem] sm:min-h-[3rem] sm:w-[3rem] sm:h-[3rem] rounded-full flex items-center justify-center text-lg font-bold ${
            rank === 1
              ? "text-yellow-400 sm:text-white sm:bg-gradient-to-r sm:from-yellow-400 sm:to-yellow-600"
              : rank === 2
              ? "text-gray-400 sm:text-white sm:bg-gradient-to-r sm:from-gray-400 sm:to-gray-600"
              : rank === 3
              ? "text-yellow-600 sm:text-white sm:bg-gradient-to-r sm:from-yellow-600 sm:to-yellow-800"
              : "text-purple-500 sm:text-white sm:bg-gradient-to-r sm:from-purple-500 sm:to-blue-600"
          }`}
        >
          {rank <= 3 ? <i className="pi pi-crown text-lg"></i> : rank}
        </span>
        <div className="col-center-start w-full">
          <div className="row-between-center gap-2 w-full">
            <span className="text-gray-800 font-semibold text-sm sm:text-base">
              {rowData.customer_name}
            </span>
            <div
              className={`text-white text-center text-xs sm:text-sm font-medium bg-gradient-to-br rounded-md min-w-[3rem] w-[3rem] sm:w-[4rem] py-1 ${
                rowData.level_name === "Gold"
                  ? "from-yellow-400 to-orange-500"
                  : rowData.level_name === "Silver"
                  ? "from-gray-400 to-gray-950"
                  : "from-green-500 to-emerald-950"
              }`}
            >
              {rowData.level_name}
            </div>
          </div>
          <span className="text-gray-600 text-xs">{rowData.customer_code}</span>
          <div className="w-full col-center-start text-xs sm:text-md text-gray-800 font-semibold mt-1">
            <span>Transactions: {formatPax(rowData.receipts)}</span>
            <span>Spend: {formatCurrency(rowData.revenue)}</span>
          </div>
        </div>
      </div>
    );
  };
  const CustomerNameTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const rank = rowIndex + 1 + paging.take * (paging.page - 1);
    return (
      <div className="row-start-center w-full gap-4">
        <span
          className={`min-w-[3rem] min-h-[3rem] w-[3rem] h-[3rem] rounded-full flex items-center justify-center text-white text-lg font-bold ${
            rank === 1
              ? "bg-gradient-to-r from-yellow-400 to-yellow-600"
              : rank === 2
              ? "bg-gradient-to-r from-gray-400 to-gray-600"
              : rank === 3
              ? "bg-gradient-to-r from-yellow-600 to-yellow-800"
              : "bg-gradient-to-r from-purple-500 to-blue-600"
          }`}
        >
          {rank <= 3 ? <i className="pi pi-crown text-lg"></i> : rank}
        </span>
        <div className="col-center-start">
          <span className="text-gray-800 font-semibold text-base">
            {rowData.customer_name}
          </span>
          <span className="text-gray-600 text-sm">{rowData.customer_code}</span>
          <div
            className={`text-white text-center text-xs font-medium bg-gradient-to-br rounded-md px-4 py-1 ${
              rowData.level_name === "Gold"
                ? "from-yellow-400 to-orange-500"
                : rowData.level_name === "Silver"
                ? "from-gray-400 to-gray-950"
                : "from-green-500 to-emerald-950"
            }`}
          >
            {rowData.level_name}
          </div>
        </div>
      </div>
    );
  };

  const transactionsBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full">{formatPax(rowData.receipts)}</div>
    );
  };

  const revenueBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full">
        {formatCurrency(rowData.revenue)}
      </div>
    );
  };

  const TableSkeleton = ({ rows = 10 }) => {
    // Create an array of dummy items to simulate rows
    const skeletonRows = Array.from({ length: rows }, (_, i) => ({ id: i }));

    // Define skeleton renderers for each column
    const nameSkeleton = () => (
      <div className="flex items-center gap-4">
        <Skeleton shape="circle" size="3rem" className="flex-shrink-0" />
        <div className="flex flex-col gap-2 w-full">
          <Skeleton width="7rem" height="1rem" />
          <Skeleton width="5rem" height="0.8rem" />
          <Skeleton width="4rem" height="1.5rem" />
        </div>
      </div>
    );

    const cardNameSkeleton = () => (
      <div className="flex items-center gap-4">
        <Skeleton shape="circle" size="3rem" className="flex-shrink-0" />
        <div className="flex flex-col gap-2 w-full">
          <div className="row-between-center w-full">
            <Skeleton width="7rem" height="1rem" />
            <Skeleton width="4rem" height="1.5rem" />
          </div>
          <Skeleton width="3rem" height="0.8rem" />
          <Skeleton width="8rem" height="1rem" />
          <Skeleton width="8rem" height="1rem" />
        </div>
      </div>
    );

    const mobileCardNameSkeleton = () => (
      <div className="flex items-center gap-2">
        <Skeleton shape="circle" size="1.5rem" className="flex-shrink-0" />
        <div className="flex flex-col gap-1 w-full">
          <div className="row-between-center w-full">
            <Skeleton width="6rem" height="1rem" />
            <Skeleton width="3rem" height="1.5rem" />
          </div>
          <Skeleton width="3rem" height="0.8rem" />
          <Skeleton width="8rem" height="1rem" />
          <Skeleton width="8rem" height="1rem" />
        </div>
      </div>
    );

    const transactionSkeleton = () => <Skeleton width="4rem" height="1rem" />;
    const revenueSkeleton = () => <Skeleton width="6rem" height="1rem" />;

    return (
      <div className="w-full border border-gray-300 rounded-xl overflow-hidden">
        <DataTable value={skeletonRows} className="w-full" stripedRows>
          <Column
            header="Name"
            headerStyle={viewType === "card" ? { display: "none" } : null}
            body={
              viewType === "card"
                ? isMobile
                  ? mobileCardNameSkeleton
                  : cardNameSkeleton
                : nameSkeleton
            }
            style={{ width: viewType === "card" ? "100%" : "50%" }}
          />
          {viewType === "card" ? null : (
            <Column header="Transactions" body={transactionSkeleton} />
          )}
          {viewType === "card" ? null : (
            <Column header="Total Spent" body={revenueSkeleton} />
          )}
        </DataTable>
      </div>
    );
  };

  const onRowSelect = (event) => {
    window.location.href = `/report/customer-detail/${event.data.customer_code}`;
  };

  return (
    <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300 sm:!px-6">
      <div className="w-full flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col items-start gap-1">
          <label className="flex items-center gap-2 text-lg font-semibold text-black">
            <i className="pi pi-user text-green-500 text-lg"></i>
            Customer Spendings
          </label>
          <span className="text-xs bg-gradient-to-br from-green-200 to-emerald-200 text-black font-semibold border border-green-400 rounded-md px-2 py-0.5">
            {format(new Date(filterValue?.dari_tanggal), "dd MMM yyyy")} -{" "}
            {format(new Date(filterValue?.sampai_tanggal), "dd MMM yyyy")}
          </span>
        </div>

        <div className="flex gap-2 rounded-md p-1 w-full sm:w-auto">
          {viewOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setViewType(option.value)}
              className={`px-4 py-1.5 w-full sm:w-auto rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out 
                  ${
                    viewType === option.value
                      ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
                      : "text-black hover:bg-green-100 bg-slate-100"
                  }`}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <TableSkeleton rows={paging.take} />
          </motion.div>
        ) : viewType === "card" ? (
          <motion.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data}
              loading={loading}
              className="w-full"
              stripedRows
              selectionMode="single"
              onRowSelect={onRowSelect}
              selection={selectedCustomer}
              onSelectionChange={(e) => setSelectedCustomer(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
            >
              <Column
                field="customer_id"
                header=""
                headerStyle={{ display: "none" }}
                body={CustomerMobileTemplate}
              />
            </DataTable>
          </motion.div>
        ) : (
          <motion.div
            key="table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data}
              loading={loading}
              className="w-full"
              stripedRows
              selectionMode="single"
              onRowSelect={onRowSelect}
              selection={selectedCustomer}
              onSelectionChange={(e) => setSelectedCustomer(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
            >
              <Column
                field="customer_name"
                header="Name"
                body={CustomerNameTemplate}
                style={{ width: "50%" }}
              />
              <Column
                field="receipts"
                header="Transactions"
                body={transactionsBodyTemplate}
              />
              <Column
                field="revenue"
                header="Total Spent"
                body={revenueBodyTemplate}
              />
            </DataTable>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full row-center-end">
        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[10, 30, 50, 100]}
          onPageChange={handleOnPage}
          template={{
            layout: isMobile
              ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
              : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
          }}
        />
      </div>
    </div>
  );
};

export default CustomerList;
